import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, CardMedia, Button, Stack, Chip } from '@mui/material';

const Product = () => {
  const productItems = [
    {
      title: 'PITWALL',
      image: 'pitwall.png',
      description:
        'Live telemetry and race strategy tool for Assetto Corsa Competizione and Assetto Corsa EVO. PITWALL provides real-time data visualization, strategy planning, and performance analysis for racing enthusiasts and teams.',
      badge: 'Assetto Corsa Competizione & Assetto Corsa EVO Tool',
      cta: {
        label: 'Try Now',
        href: 'https://pitwallracing.com/',
      },
    },
    {
      title: 'Trader for Luno',
      image: 'luno.png',
      description:
        'Trader for Luno is an automatic cryptocurrency trading platform built for the Luno market, giving users direct control over trading rules, risk preferences, strategy behavior, and their own Luno authentication keys.',
      badge: 'Automated Trading',
      cta: {
        label: 'More Info',
        href: '/trader',
      },
    },
    {
      title: 'Vaya-Taxi',
      image: 'vaya-taxi.png',
      description:
        'Vaya Taxi is community-powered safety for everyone. Please rate the taxi you’re in to help others make safer choices. The more ratings we collect, the better we can protect one another.',
      badge: 'Mobile Web App',
      cta: {
        label: 'Check it Out',
        href: 'https://www.vaya-taxi.co.za/',
      },
    },
    {
      title: 'Fake or Frame',
      image: 'fof.png',
      description:
        'Fake or Frame is a swipe left/right game that tests your ability to distinguish between real and AI-generated videos. It’s a fun way to explore the capabilities of generative AI and challenge your perception.',
      badge: 'Web Game',
      cta: {
        label: 'Play Now',
        href: 'https://www.fakeorframe.com/',
      },
    },
    {
      title: 'Global Interviewer',
      image: 'global_interviewer.png',
      description:
        "Global Interviewer is an AI-powered platform that helps professionals present their experience in a more conversational, memorable format. It's designed to lower friction while increasing visibility.",
      badge: 'AI Networking',
    },
    {
      title: 'Global Recruiter',
      image: 'global_recruiter.png',
      description:
        'Global Recruiter streamlines sourcing through an on-demand assistant model, giving recruiters direct access to relevant candidates without the overhead of a traditional platform.',
      badge: 'Recruitment Assistant',
    }
  ];

  return (
    <Box
      id="product"
      sx={{
        py: { xs: 6, md: 9 },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={1.5} sx={{ mb: { xs: 3, md: 5 }, maxWidth: 740 }}>
          <Typography
            variant="overline"
            sx={{ color: 'secondary.main', letterSpacing: '0.16em', fontWeight: 700 }}
          >
            Products
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{ fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' }, color: 'primary.main' }}
          >
            Built for practical use, not just concept demos.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.08rem' } }}
          >
            Our products focus on solving concrete problems. We prioritize real-world utility and user experience over flashy demos, ensuring that our solutions deliver tangible value.
          </Typography>
        </Stack>

        <Grid container spacing={{ xs: 2.5, md: 3 }}>
          {productItems.map((product) => (
            <Grid item xs={12} md={4} key={product.title}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid rgba(16, 35, 63, 0.08)',
                  background:
                    product.title === 'Golden Oldies LAN'
                      ? 'linear-gradient(180deg, #fff8ea 0%, #fffef7 100%)'
                      : '#ffffff',
                  boxShadow: '0 22px 45px rgba(16, 35, 63, 0.08)',
                  transition: 'all 0.35s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 28px 60px rgba(16, 35, 63, 0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 220,
                    background:
                      product.title === 'Golden Oldies LAN'
                        ? 'radial-gradient(circle at center, rgba(212, 175, 55, 0.22), transparent 65%)'
                        : 'linear-gradient(180deg, #f7fafc 0%, #eef5fb 100%)',
                  }}
                >
                  {product.image ? (
                    <CardMedia
                      component="img"
                      image={product.image}
                      alt={product.title}
                      sx={{
                        width: 150,
                        height: 150,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 110,
                        height: 110,
                        borderRadius: '50%',
                        border: '2px solid #37a7d4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.65)',
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          color:
                            product.title === 'Golden Oldies LAN' ? '#189fc8' : 'primary.main',
                          fontWeight: 800,
                          fontSize: product.title === 'Trader for Luno' ? '1.7rem' : undefined,
                        }}
                      >
                        {product.title === 'Golden Oldies LAN' ? 'LAN' : 'LT'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Chip
                    label={product.badge}
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      mb: 1.5,
                      backgroundColor: 'rgba(32, 198, 183, 0.12)',
                      color: 'secondary.main',
                      fontWeight: 700,
                    }}
                  />
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{ color: 'primary.main', fontWeight: 800, mb: 1.25 }}
                  >
                    {product.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {product.description}
                  </Typography>
                  {product.cta ? (
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        component="a"
                        href={product.cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          backgroundColor: '#3776d4',
                          color: '#e9e9e9',
                          '&:hover': {
                            backgroundColor: '#1f44be',
                          },
                        }}
                      >
                        {product.cta.label}
                      </Button>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Product;
